import React from 'react';
import { Link } from 'react-router-dom';
import {
  Store, DollarSign, Video, CalendarDays, Upload, Check,
  ArrowRight, Link2, Share2, BarChart3,
} from 'lucide-react';

// Official Hey Joe brand tokens (from the brand kit — not the app's remapped --hj set).
const CREAM = '#FCF4E6';
const INK = '#111827';      // gray-900 — headings
const INK2 = '#595F6A';     // gray-500 — secondary text
const BLUE = '#4171F9';     // links / accents
const YELLOW = '#FAD202';   // highlight / upsell
const PINK = '#F46AB9';
const GREEN = '#8CC640';
const BORDER = '#E5E7EB';   // gray-200
const HEAD = "'Red Hat Display', system-ui, sans-serif";
const BODY = "'Helvetica Neue', Arial, sans-serif";
const CARD_SHADOW = '0 1px 3px rgba(0,0,0,.08), 0 1px 2px rgba(0,0,0,.04)';

const card: React.CSSProperties = {
  background: '#ffffff',
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  boxShadow: CARD_SHADOW,
};

const Bullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="flex items-start gap-2.5" style={{ padding: '5px 0', fontSize: 14.5, color: INK, lineHeight: 1.55, fontFamily: BODY }}>
    <span className="flex items-center justify-center flex-shrink-0" style={{ marginTop: 2, width: 18, height: 18, borderRadius: 999, background: 'rgba(140,198,64,.16)' }}>
      <Check className="w-3 h-3" style={{ color: GREEN }} strokeWidth={3} />
    </span>
    <span>{children}</span>
  </li>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 15, letterSpacing: '.02em', color: INK2, textTransform: 'uppercase', margin: '0 0 16px' }}>
    {children}
  </h2>
);

const CreatorGuidePage: React.FC = () => {
  return (
    <main className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: BODY }}>
      <div className="mx-auto" style={{ maxWidth: 960, padding: '0 20px 96px' }}>

        {/* Header */}
        <header className="text-center relative" style={{ padding: '48px 0 34px' }}>
          <img src="/lovable-uploads/heyjoe-logo-black.png" alt="Hey Joe" style={{ height: 34, width: 'auto', margin: '0 auto 26px', display: 'block' }} />
          <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 13, letterSpacing: '.08em', textTransform: 'uppercase', color: BLUE, marginBottom: 12 }}>
            Creator guide
          </div>
          <div className="relative inline-block">
            <h1 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 'clamp(34px, 5.2vw, 56px)', lineHeight: 1.02, letterSpacing: '-0.02em', color: INK, margin: 0 }}>
              Create a course or featured event
            </h1>
            {/* one yellow scribble accent (brand rule: max one per page) */}
            <svg width="260" height="14" viewBox="0 0 260 14" fill="none" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: -12 }} aria-hidden="true">
              <path d="M3 8 Q 33 1, 63 7 T 123 7 T 183 7 T 257 6" stroke={YELLOW} strokeWidth="5" strokeLinecap="round" fill="none" />
            </svg>
          </div>
          <p className="mx-auto" style={{ margin: '26px auto 0', maxWidth: 560, fontSize: 16, lineHeight: 1.55, color: INK2 }}>
            Set up your store and list on the Hey Joe marketplace in a few minutes. Selling is free — we take a small commission only on paid sales.
          </p>
          <Link
            to="/my-store"
            className="inline-flex items-center gap-2"
            style={{ marginTop: 24, padding: '13px 26px', borderRadius: 6, background: '#000', color: '#fff', fontFamily: HEAD, fontWeight: 600, fontSize: 15, textDecoration: 'none' }}
          >
            Open My Store <ArrowRight className="w-4 h-4" />
          </Link>
        </header>

        {/* Get set up */}
        <section>
          <SectionLabel>Get set up</SectionLabel>
          <div className="flex flex-col gap-3">
            {[
              { icon: <Store className="w-4 h-4" style={{ color: BLUE }} />, title: 'Become a Creator — free', body: <>Go to <strong>My Store</strong> and click <strong>“Start selling — Free.”</strong> Your Creator account activates instantly. No monthly fee.</> },
              { icon: <Share2 className="w-4 h-4" style={{ color: BLUE }} />, title: 'Set up your store page', body: <>Add your <strong>Display Name</strong> (your public brand), a <strong>URL slug</strong> (your store lives at <code style={{ fontSize: 13 }}>plus.heyjoe.io/creators/your-slug</code>), and a <strong>profile image</strong>.</> },
              { icon: <DollarSign className="w-4 h-4" style={{ color: BLUE }} />, title: 'Connect Stripe to get paid', body: <>Connect <strong>Stripe</strong> so sales pay out to your bank — you keep <strong>80%</strong> of every sale. A fully free featured event doesn’t need this, but set it up so you’re ready to charge.</> },
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-4" style={{ ...card, padding: 20 }}>
                <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, background: 'rgba(65,113,249,.12)', color: BLUE, fontFamily: HEAD, fontWeight: 800, fontSize: 18 }}>
                  {i + 1}
                </div>
                <div>
                  <div className="flex items-center gap-2" style={{ fontFamily: HEAD, fontSize: 17, fontWeight: 700, color: INK }}>
                    {s.icon}{s.title}
                  </div>
                  <p className="m-0" style={{ marginTop: 6, fontSize: 14.5, lineHeight: 1.55, color: INK2 }}>{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Create your listing */}
        <section style={{ marginTop: 40 }}>
          <SectionLabel>Create your listing</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Featured event */}
            <div style={{ ...card, padding: 24 }}>
              <div className="flex items-center gap-2.5" style={{ marginBottom: 4 }}>
                <div className="flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(250,210,2,.20)', color: '#9a8200' }}>
                  <CalendarDays className="w-5 h-5" />
                </div>
                <h3 style={{ fontFamily: HEAD, fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', color: INK, margin: 0 }}>Live featured event</h3>
              </div>
              <p className="m-0" style={{ marginBottom: 14, fontSize: 13.5, color: INK2, lineHeight: 1.5 }}>A scheduled live event or class. No video needed.</p>
              <ul className="m-0 p-0" style={{ listStyle: 'none' }}>
                <Bullet>New product → choose <strong>“Live Featured event / Class.”</strong></Bullet>
                <Bullet><strong>Title</strong>, <strong>description</strong>, and <strong>price</strong> (leave $0 for free, or set a price).</Bullet>
                <Bullet><strong>Date &amp; time</strong> (must be in the future).</Bullet>
                <Bullet><strong>Join link</strong> — your Zoom / Meet / room URL, and <strong>Duration</strong>.</Bullet>
                <Bullet>Toggle <strong>Published</strong>, then <strong>Save</strong>.</Bullet>
              </ul>
              <div className="flex items-start gap-2" style={{ marginTop: 12, padding: 12, borderRadius: 6, background: 'rgba(250,210,2,.12)', borderLeft: `3px solid ${YELLOW}` }}>
                <Link2 className="w-4 h-4 flex-shrink-0" style={{ marginTop: 1, color: '#9a8200' }} />
                <span style={{ fontSize: 13, lineHeight: 1.5, color: INK }}>
                  <strong>Have your join link ready first.</strong> It must be a real <code style={{ fontSize: 12 }}>https://</code> URL — the form requires it.
                </span>
              </div>
              <p className="m-0" style={{ marginTop: 12, fontSize: 12.5, color: INK2, lineHeight: 1.5 }}>
                Registrants automatically get a confirmation email with the join link + calendar buttons.
              </p>
            </div>

            {/* Course */}
            <div style={{ ...card, padding: 24 }}>
              <div className="flex items-center gap-2.5" style={{ marginBottom: 4 }}>
                <div className="flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(65,113,249,.12)', color: BLUE }}>
                  <Video className="w-5 h-5" />
                </div>
                <h3 style={{ fontFamily: HEAD, fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', color: INK, margin: 0 }}>On-demand course</h3>
              </div>
              <p className="m-0" style={{ marginBottom: 14, fontSize: 13.5, color: INK2, lineHeight: 1.5 }}>Video or files people can buy and watch anytime.</p>
              <ul className="m-0 p-0" style={{ listStyle: 'none' }}>
                <Bullet>New product → choose <strong>“Single Item”</strong> (one video/file) or <strong>“Series / Bundle”</strong> (multi-lesson).</Bullet>
                <Bullet><strong>Title</strong>, <strong>description</strong>, and <strong>price</strong> (free or paid).</Bullet>
                <Bullet>Add content: pick a video from <strong>your Hey Joe projects</strong>, or <strong>upload a file</strong>.</Bullet>
                <Bullet>Toggle <strong>Published</strong>, then <strong>Save</strong>.</Bullet>
              </ul>
              <div className="flex items-start gap-2" style={{ marginTop: 12, padding: 12, borderRadius: 6, background: 'rgba(65,113,249,.08)', borderLeft: `3px solid ${BLUE}` }}>
                <Upload className="w-4 h-4 flex-shrink-0" style={{ marginTop: 1, color: BLUE }} />
                <span style={{ fontSize: 13, lineHeight: 1.5, color: INK }}>Uploads accept PDF, images, video, audio, or ZIP — up to <strong>500MB</strong>.</span>
              </div>
            </div>
          </div>
        </section>

        {/* After it's live */}
        <section style={{ marginTop: 40 }}>
          <SectionLabel>After it’s live</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: <Share2 className="w-4 h-4" style={{ color: BLUE }} />, t: 'Share your store', b: <>Your link is <code style={{ fontSize: 11.5 }}>plus.heyjoe.io/creators/your-slug</code> — copy it from My Store.</> },
              { icon: <BarChart3 className="w-4 h-4" style={{ color: BLUE }} />, t: 'Track sales', b: <><strong>My Store → Sales</strong> shows every buyer/registrant and your earnings.</> },
              { icon: <DollarSign className="w-4 h-4" style={{ color: BLUE }} />, t: 'Get paid', b: <>You keep <strong>80%</strong>; Stripe deposits paid sales to your bank automatically.</> },
            ].map((x, i) => (
              <div key={i} style={{ ...card, padding: 18 }}>
                <div className="flex items-center gap-2" style={{ fontFamily: HEAD, fontSize: 15, fontWeight: 700, color: INK, marginBottom: 6 }}>
                  {x.icon}{x.t}
                </div>
                <p className="m-0" style={{ fontSize: 13, lineHeight: 1.5, color: INK2 }}>{x.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA footer */}
        <div className="text-center" style={{ marginTop: 48 }}>
          <Link
            to="/my-store"
            className="inline-flex items-center gap-2"
            style={{ padding: '14px 30px', borderRadius: 6, background: '#000', color: '#fff', fontFamily: HEAD, fontWeight: 600, fontSize: 15.5, textDecoration: 'none' }}
          >
            Start in My Store <ArrowRight className="w-4 h-4" />
          </Link>
          <p style={{ marginTop: 14, fontSize: 12.5, color: INK2 }}>
            Questions? <a href="mailto:support@heyjoe.io" style={{ color: BLUE, textDecoration: 'none' }}>We’re here to help.</a>
          </p>
        </div>
      </div>
    </main>
  );
};

export default CreatorGuidePage;
